import React, { useMemo } from "react";
import { Flex, Small, Icon, Popover, Badge, Loading } from "orcs-design-system";
import styled from "styled-components";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { useWorkspaceTagConfig } from "src/contexts/global/WorkspaceContext";
import { getTagTypeName } from "src/util/tags";
import urlPaths, { url } from "src/consts/urlPaths";
import TeamsListTooltip from "./TeamsListTooltip";

const TagItemContainer = styled(Flex)`
  width: fit-content;
  max-width: 100%;
  justify-content: flex-start;
  align-items: center;
  padding: 5px 0px;
  margin-left: 5px !important;
  min-height: 35px;
`;

const TagName = styled(Small)`
  font-weight: 500;
  color: #333;
  flex: 1;
  min-width: 0;
  padding: 5px 0px;
  border-radius: ${({ theme }) => theme.radii[2]};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryLightest};
  }
`;

const ChildIcon = styled.div`
  margin: 0 0 0 6px;
  display: flex;
  align-items: center;
  padding: 3px 10px;
  height: 22px;
  color: #666;

  svg {
    font-size: 1.4rem;
  }
`;

const ChildCount = styled(Small)`
  color: #666;
  padding: 3px 10px;
  font-size: 1rem;
  font-weight: 700;
  margin-left: 0;
  flex-shrink: 0;
  height: 23px;
  display: flex;
  align-items: center;
  cursor: pointer;

  svg {
    font-size: 1.2rem;
  }
`;

const TagTreeHeader = ({ node, loadingNodeIds }) => {
  const tagConfig = useWorkspaceTagConfig();
  // Handle both EntityTag format (node.tag.tag) and direct Tag format (node.tag)
  const actualTag = node?.tag?.tag || node?.tag;

  const totalChildCount = actualTag?.childTags?.total || 0;

  const appliedEntities = useMemo(
    () => actualTag?.appliedEntities?.appliedEntities || [],
    [actualTag?.appliedEntities?.appliedEntities]
  );

  const teamsEntities = useMemo(
    () => appliedEntities.filter((entity) => entity.group?.id),
    [appliedEntities]
  );

  const entityChildCount = teamsEntities.length;

  const hasChildren = totalChildCount > 0;
  const hasTeams = entityChildCount > 0;
  const linkPath = url(urlPaths.TAG_EXPLORE, { id: node.id });
  const isLoadingChildren = loadingNodeIds?.[node.id] || false;

  // Check if this is a direct child node
  // We use the isChild property that was set when the tree was created
  const isDirectChild = node?.isChild === true;

  // Get tag type name for child tags
  const tagTypeName = isDirectChild
    ? getTagTypeName(actualTag?.type, tagConfig)
    : null;

  return (
    <TagItemContainer>
      <Link to={linkPath} style={{ textDecoration: "none" }}>
        <TagName isChildNode={isDirectChild}>
          {node.name || "Unknown Tag"}
        </TagName>
      </Link>
      {isDirectChild && tagTypeName && (
        <Badge style={{ marginLeft: "8px" }}>{tagTypeName}</Badge>
      )}
      {hasChildren && (
        <ChildIcon>
          <Icon
            icon={["fas", "level-up"]}
            size="sm"
            transform={{ rotate: 90 }}
          />
          <span
            style={{
              marginLeft: "4px",
              fontSize: "1rem",
              fontWeight: "700",
            }}
          >
            {totalChildCount}
          </span>
        </ChildIcon>
      )}
      {hasTeams && (
        <Popover
          text={<TeamsListTooltip appliedEntities={teamsEntities} />}
          direction="top"
          withFocusControl={false}
          width="fit-content"
        >
          <ChildCount>
            <Icon icon={["fas", "users"]} style={{ marginRight: "6px" }} />
            {entityChildCount}
          </ChildCount>
        </Popover>
      )}
      {isLoadingChildren && <Loading primary small />}
    </TagItemContainer>
  );
};

TagTreeHeader.propTypes = {
  node: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    isChild: PropTypes.bool,
    tag: PropTypes.shape({
      tag: PropTypes.object,
      type: PropTypes.string,
      appliedEntities: PropTypes.shape({
        appliedEntities: PropTypes.arrayOf(
          PropTypes.shape({
            relationshipType: PropTypes.string,
            group: PropTypes.shape({
              id: PropTypes.string,
              name: PropTypes.string,
            }),
          })
        ),
      }),
      childTags: PropTypes.shape({
        total: PropTypes.number,
      }),
    }),
  }).isRequired,
  loadingNodeIds: PropTypes.object,
};

export default TagTreeHeader;
